import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { listSnapEvents, sendSnapTestEvent } from "@/lib/snap.functions";

export const Route = createFileRoute("/snap-capi")({
  component: SnapCapiPage,
  head: () => ({
    meta: [
      { title: "Snapchat Conversions — Inquira Admin" },
      { name: "description", content: "Admin-only delivery log for Inquira's Snapchat app conversion events." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Snapchat Conversions — Inquira Admin" },
      { property: "og:description", content: "Admin-only Snapchat conversion delivery log." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type Ev = {
  id: string; event_name: string; platform: string; origin: string; status: string;
  http_status: number | null; attempts: number; error: string | null; is_test: boolean; created_at: string;
};
const EVENTS = ["APP_INSTALL", "APP_OPEN", "SIGN_UP", "START_TRIAL", "PURCHASE"] as const;

function SnapCapiPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const list = useServerFn(listSnapEvents);
  const test = useServerFn(sendSnapTestEvent);
  const [events, setEvents] = useState<Ev[]>([]);
  const [token, setToken] = useState<boolean | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android">("android");

  const load = useCallback(async () => {
    try {
      const r = await list();
      setEvents(r.events as Ev[]);
      setToken(r.tokenConfigured);
      setErr(null);
    } catch {
      setErr(t("snapAdmin.loadError"));
    }
  }, [list]);

  useEffect(() => { if (user) void load(); }, [user, load]);

  const sendTest = async (eventName: (typeof EVENTS)[number], platform: "ios" | "android") => {
    setBusy(true);
    try {
      const r = await test({ data: { eventName, platform } });
      if (r.status === "validated") toast.success(`${t("snapAdmin.accepted")}: ${eventName}`);
      else toast.error(`${t("snapAdmin.rejected")}: ${eventName}`, { description: r.detail });
      await load();
    } catch {
      toast.error(t("snapAdmin.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-4xl space-y-5">
        <Link to="/admin" className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground">
          <ArrowLeft className="size-4" /> {t("snapAdmin.back")}
        </Link>
        <h1 className="text-2xl font-bold text-foreground">{t("snapAdmin.title")}</h1>
        {!user && <p className="text-muted-foreground">{t("snapAdmin.signIn")}</p>}
        {user && err && <p className="text-destructive">{err}</p>}
        {user && !err && (
          <>
            <p className="text-sm text-muted-foreground">
              {t("snapAdmin.keyLabel")}: {token == null ? "…" : token ? t("snapAdmin.configured") : t("snapAdmin.missing")}. {t("snapAdmin.testNote")}
            </p>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-muted-foreground">{t("snapAdmin.androidStatus")}</dt><dd>{t("snapAdmin.nativeRequired")}</dd></div>
              <div><dt className="text-muted-foreground">{t("snapAdmin.iosStatus")}</dt><dd>{t("snapAdmin.nativeRequired")}</dd></div>
              <div><dt className="text-muted-foreground">{t("snapAdmin.productionStatus")}</dt><dd>{events.some((e) => !e.is_test && e.status === "sent") ? t("snapAdmin.apiAccepted") : t("snapAdmin.unverified")}</dd></div>
              <div><dt className="text-muted-foreground">{t("snapAdmin.managerStatus")}</dt><dd>{t("snapAdmin.unverified")}</dd></div>
              {["validated", "failed"].map((status) => <div key={status}><dt className="text-muted-foreground">{t(status === "validated" ? "snapAdmin.lastValidation" : "snapAdmin.lastFailure")}</dt><dd>{events.find((e) => e.status === status)?.created_at ? new Date(events.find((e) => e.status === status)?.created_at ?? "").toLocaleString() : t("snapAdmin.none")}</dd></div>)}
            </dl>
            <div className="flex flex-wrap gap-2">
              {import.meta.env.DEV && <>
              <div className="flex gap-1" role="group" aria-label={t("snapAdmin.app")}>
                {(["android", "ios"] as const).map((p) => <Button key={p} variant={platform === p ? "default" : "outline"} aria-pressed={platform === p} onClick={() => setPlatform(p)}>{p === "ios" ? "iOS" : "Android"}</Button>)}
              </div>
              {EVENTS.map((e) => (
                <Button key={e} size="sm" variant="outline" disabled={busy} onClick={() => sendTest(e, platform)} className="min-h-11">
                  <Send className="size-4" /> {t("snapAdmin.test")} {e}
                </Button>
              ))}
              </>}
              <Button size="sm" variant="ghost" onClick={load} className="min-h-11"><RefreshCw className="size-4" /> {t("snapAdmin.refresh")}</Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr><th className="p-2">{t("snapAdmin.time")}</th><th className="p-2">{t("snapAdmin.event")}</th><th className="p-2">{t("snapAdmin.app")}</th><th className="p-2">{t("snapAdmin.from")}</th><th className="p-2">{t("snapAdmin.result")}</th><th className="p-2">{t("snapAdmin.detail")}</th></tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="p-2 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="p-2">{e.event_name}{e.is_test ? ` ${t("snapAdmin.testSuffix")}` : ""}</td>
                      <td className="p-2">{e.platform}</td>
                      <td className="p-2">{e.origin}</td>
                      <td className={`p-2 font-medium ${e.status === "failed" ? "text-destructive" : "text-foreground"}`}>
                        {e.status}{e.http_status ? ` · ${e.http_status}` : ""}{e.attempts > 1 ? ` · ${e.attempts} ${t("snapAdmin.tries")}` : ""}
                      </td>
                      <td className="p-2 max-w-xs truncate text-muted-foreground" title={e.error ?? ""}>{e.error ?? ""}</td>
                    </tr>
                  ))}
                  {events.length === 0 && <tr><td className="p-4 text-muted-foreground" colSpan={6}>{t("snapAdmin.none")}</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
